import { useQueryClient } from '@tanstack/react-query';
import { useModeAwareQuery, useModeAwareMutation } from '@/hooks/useModeAwareQuery';
import { apiGet, apiPost, apiDelete } from '@/lib/api-client';
import { INTEGRATION_DEFINITIONS } from '@/lib/integrations-data';
import type { IntegrationDefinition, IntegrationConnection, AccessFinancialsConfig } from '@/types/integrations';

// ── API response shapes ───────────────────────────────────────────────────────

interface IntegrationApiResponse {
  id: number;
  name: string;
  category: string;
  status: string;
  requiresOauth: boolean;
  configJson: string | null;
  createdAt: string;
  updatedAt: string | null;
  connectionCount: number;
}

interface ConnectionApiResponse {
  id: number;
  integrationId: number;
  integrationName: string;
  status: string;
  connectedAccount: string | null;
  lastSync: string | null;
  configJson: string | null;
  createdAt: string;
}

// ── Demo / mock data ──────────────────────────────────────────────────────────

const MOCK_INTEGRATIONS_API: IntegrationApiResponse[] = INTEGRATION_DEFINITIONS.map((def, i) => ({
  id: i + 1,
  name: def.name,
  category: def.category,
  status: 'active',
  requiresOauth: def.requiresOAuth,
  configJson: null,
  createdAt: new Date().toISOString(),
  updatedAt: null,
  connectionCount: 0,
}));

const accessFinancialsId = MOCK_INTEGRATIONS_API.find(i => i.name === 'Access Financials')?.id ?? 1;
const outlookId = MOCK_INTEGRATIONS_API.find(i => i.name === 'Outlook')?.id ?? 8;

const MOCK_CONNECTIONS_API: ConnectionApiResponse[] = [
  {
    id: 1,
    integrationId: accessFinancialsId,
    integrationName: 'Access Financials',
    status: 'active',
    connectedAccount: null,
    lastSync: new Date().toISOString(),
    configJson: JSON.stringify({ serverUrl: 'https://server.acme.com', database: 'AcmeDB', username: 'admin' }),
    createdAt: new Date().toISOString(),
  },
  {
    id: 2,
    integrationId: outlookId,
    integrationName: 'Outlook',
    status: 'active',
    connectedAccount: 'user@acme.com',
    lastSync: new Date().toISOString(),
    configJson: null,
    createdAt: new Date().toISOString(),
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function normaliseName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function mapConnection(conn: ConnectionApiResponse): IntegrationConnection & { backendIntegrationId: number } {
  return {
    id: String(conn.id),
    integrationId: String(conn.integrationId),
    companyId: '',
    status: conn.status === 'active' ? 'connected' : conn.status === 'error' ? 'error' : 'connected',
    credentials: {},
    connectedAccount: conn.connectedAccount ?? undefined,
    lastSync: conn.lastSync ?? undefined,
    config: conn.configJson ? JSON.parse(conn.configJson) : undefined,
    createdAt: conn.createdAt,
    backendIntegrationId: conn.integrationId,
  };
}

// ── Queries ───────────────────────────────────────────────────────────────────

function useIntegrationsApi() {
  return useModeAwareQuery<IntegrationApiResponse[]>(
    ['integrations'],
    ({ signal }) => apiGet<IntegrationApiResponse[]>('/settings/integrations', undefined, signal),
    MOCK_INTEGRATIONS_API,
    { staleTime: 30_000 }
  );
}

function useConnectionsApi() {
  return useModeAwareQuery<ConnectionApiResponse[]>(
    ['integrations', 'connections'],
    ({ signal }) => apiGet<ConnectionApiResponse[]>('/settings/connections', undefined, signal),
    MOCK_CONNECTIONS_API,
    { staleTime: 30_000 }
  );
}

// ── Merged view ───────────────────────────────────────────────────────────────

export interface IntegrationWithConnection {
  definition: IntegrationDefinition;
  backendId: number;
  connection: (IntegrationConnection & { backendIntegrationId: number }) | undefined;
}

export function useIntegrationsWithConnections(): {
  data: IntegrationWithConnection[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data: apiIntegrations = [], isLoading: loadingIntegrations, isError: errorIntegrations } = useIntegrationsApi();
  const { data: apiConnections = [], isLoading: loadingConnections, isError: errorConnections } = useConnectionsApi();

  const connectionsByIntegrationId = new Map<number, ConnectionApiResponse>(
    apiConnections.map(c => [c.integrationId, c])
  );

  const data: IntegrationWithConnection[] = INTEGRATION_DEFINITIONS.map(definition => {
    const backendIntegration = apiIntegrations.find(
      i => normaliseName(i.name) === normaliseName(definition.name)
    );
    const backendId = backendIntegration?.id ?? 0;
    const rawConnection = backendId ? connectionsByIntegrationId.get(backendId) : undefined;
    const connection = rawConnection ? mapConnection(rawConnection) : undefined;

    return { definition, backendId, connection };
  });

  return {
    data,
    isLoading: loadingIntegrations || loadingConnections,
    isError: errorIntegrations || errorConnections,
  };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface CreateConnectionPayload {
  backendIntegrationId: number;
  connectedAccount?: string;
  configJson?: string;
}

export function useCreateConnection() {
  const queryClient = useQueryClient();

  return useModeAwareMutation<ConnectionApiResponse, Error, CreateConnectionPayload>(
    ({ backendIntegrationId, connectedAccount, configJson }) =>
      apiPost<ConnectionApiResponse>(
        `/settings/integrations/${backendIntegrationId}/connections`,
        { connectedAccount, configJson }
      ),
    ({ backendIntegrationId, connectedAccount, configJson }) => ({
      id: Date.now(),
      integrationId: backendIntegrationId,
      integrationName: MOCK_INTEGRATIONS_API.find(i => i.id === backendIntegrationId)?.name ?? '',
      status: 'active',
      connectedAccount: connectedAccount ?? null,
      lastSync: new Date().toISOString(),
      configJson: configJson ?? null,
      createdAt: new Date().toISOString(),
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
      },
    }
  );
}

export function useDeleteConnection() {
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, { backendIntegrationId: number; connectionId: string }>(
    ({ backendIntegrationId, connectionId }) =>
      apiDelete(`/settings/integrations/${backendIntegrationId}/connections/${connectionId}`),
    () => undefined,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
      },
    }
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────────

export function buildConfigJson(
  credentials: Record<string, string> | AccessFinancialsConfig
): string {
  return JSON.stringify(credentials);
}
